import { createAsyncThunk } from "@reduxjs/toolkit"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IFetchedPoliciesExport } from "@common-shared/types/policies.types"

import { PoliciesListHelper } from "../../helpers/policiesList.helper"
import { PoliciesExportService } from "../../services/policiesExport.service"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import type { RootState } from "../store"

/**
 * Export all policies with applied filters and sort to CSV and email to user
 */
export const policiesExportListToEmail = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        viewType: PoliciesListType
    }>
>("policiesExportListToEmailPrefix", async (action, { dispatch, getState }) => {
    dispatch(runtimeStartLoading("policiesExportListToEmailLoading"))

    try {
        const globalState = getState() as RootState

        const { filtersConfiguration, sortConfiguration } =
            PoliciesListHelper.getViewTypedStateData(
                action.payload.viewType,
                globalState.policies,
            )

        const neededColumns = PoliciesListHelper.getColumnsConfiguration(
            action.payload.viewType,
            globalState.userDetails.selectedPracticeRole?.availableModalities,
            true,
        )

        const result = await PoliciesExportService.exportPoliciesListToEmail(
            action.practice,
            action.payload.viewType,
            filtersConfiguration,
            sortConfiguration,
            neededColumns.map(item => item.columnKey),
        )

        if (result?.triggered) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesExportListToEmailLoading"))

        action.onFinally?.()
    }
})

/**
 * Get previously generated export data from server / storage
 */
export const policiesExportGetGenerated = createAsyncThunk<
    IFetchedPoliciesExport | null,
    IThunkActionWithPracticeData<{
        exportFileName: string
    }>
>("policiesExportGetGeneratedPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesExportGetGeneratedLoading"))

    try {
        const result = await PoliciesExportService.getPoliciesExport(
            action.practice,
            action.payload.exportFileName,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || null
    } catch (e) {
        return null
    } finally {
        dispatch(runtimeStopLoading("policiesExportGetGeneratedLoading"))

        action.onFinally?.()
    }
})
